import { CoachingPracticeAudio } from '@euler/model/coaching-practice';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { lineVoiceOf } from './lineVoiceOf';
import { PracticeScene } from './practiceScene';
import { ClipProgress, useClipPlayer } from './useClipPlayer';
import { useLineReplay } from './useLineReplay';
import { PracticeSession } from './usePracticeSession';

/** How much of the customer's line being voiced is shown. */
export interface LineReveal {
  /** Null until the line is sent and its turn known. */
  turn: number | null;
  /** Characters shown: as many as have been heard. */
  shown: number;
}

/** A customer line's voice, as far as it has arrived. */
export interface LineVoice {
  /** Its sentences' levels, end to end. */
  levels: number[];
  durationMs: number;
}

/** How far into a line the voice playing is. */
export interface LinePosition {
  turn: number;
  ms: number;
}

/**
 * The customer's voice in a scene: each reply spoken as it streams (while
 * not muted), and any line heard again on tap. Talking or a new line stops
 * both; muting stops a tapped line too. A voiced line's words show as they
 * are heard, the way a caption follows speech.
 */
export function useScenePlayback(
  scene: PracticeScene,
  session: PracticeSession,
  speak$: BehaviorSubject<boolean>,
) {
  const player = useClipPlayer(speak$);
  const voices$ = useCreation(
    () => new BehaviorSubject<ReadonlyMap<number, LineVoice>>(new Map()),
    [],
  );
  // The clips heard so far, by turn; a reply streamed again replaces them.
  const heard = useRef(new Map<number, CoachingPracticeAudio[]>());
  const keep = useCallback(
    (turn: number, clips: CoachingPracticeAudio[]) => {
      heard.current.set(turn, clips);
      const voices = new Map(voices$.getValue());
      const voice = lineVoiceOf(clips);
      if (voice) voices.set(turn, voice);
      else voices.delete(turn);
      voices$.next(voices);
    },
    [voices$],
  );
  const remember = useCallback(
    (clip: CoachingPracticeAudio) => {
      const list = heard.current.get(clip.turn) ?? [];
      keep(
        clip.turn,
        list.some(x => x.seq >= clip.seq) ? [clip] : [...list, clip],
      );
    },
    [keep],
  );
  const replay = useLineReplay(
    // A replay's own lines are not the practice's: only heard ones play.
    scene.target.kind === 'practice' ? scene.target.attemptId : null,
    useCallback((turn: number) => heard.current.get(turn), []),
    useCallback(
      // A line voiced only in part is fetched again on the next tap, as
      // the server synthesises it again.
      (clips: CoachingPracticeAudio[]) => {
        if (clips.length && clips.every(x => x.audio))
          keep(clips[0].turn, clips);
      },
      [keep],
    ),
  );
  const subjects = useCreation(
    () => ({
      voices$,
      /** Ticks while a line plays; null when none does. */
      position$: new BehaviorSubject<LinePosition | null>(null),
      /** The customer is heard, live or on tap: not silence. */
      speaking$: new BehaviorSubject(false),
      /** The turn being heard. */
      playingTurn$: new BehaviorSubject<number | null>(null),
      /** Null: every line shows in full. */
      reveal$: new BehaviorSubject<LineReveal | null>(null),
    }),
    [voices$],
  );
  // The line being voiced: characters heard of each sentence, by seq.
  const voicing = useRef<{
    turn: number | null;
    heard: Map<number, number>;
  } | null>(null);
  const showAll = useCallback(() => {
    voicing.current = null;
    subjects.reveal$.next(null);
  }, [subjects]);
  const startVoicing = useCallback(
    (turn: number | null) => {
      voicing.current = { turn, heard: new Map() };
      subjects.reveal$.next({ turn, shown: 0 });
    },
    [subjects],
  );
  useEffect(() => {
    const progress = player.progress$.subscribe(({ clip, fraction }) => {
      // A reply's turn arrives before its voice: a clip heard before then
      // is the previous line's, still playing out.
      const current = voicing.current;
      if (!current || current.turn !== clip.turn) return;
      current.heard.set(clip.seq, Math.round(fraction * clip.text.length));
      let shown = 0;
      current.heard.forEach(x => (shown += x));
      if (shown !== subjects.reveal$.getValue()?.shown)
        subjects.reveal$.next({ turn: clip.turn, shown });
    });
    // A reply is voiced when it was asked for unmuted.
    const replies = session.phase$.subscribe(phase => {
      if (phase.kind !== 'replying') return;
      if (speak$.getValue()) startVoicing(null);
      else showAll();
    });
    // Nothing left to hear: the reply is in and its last sentence played.
    const quiet = combineLatest([player.playing$, session.phase$]).subscribe(
      ([playing, phase]) => {
        if (voicing.current && !playing && phase.kind !== 'replying') showAll();
      },
    );
    const muted = speak$.subscribe(speak => {
      if (!speak) showAll();
    });
    return () => {
      progress.unsubscribe();
      replies.unsubscribe();
      quiet.unsubscribe();
      muted.unsubscribe();
    };
  }, [player, session, showAll, speak$, startVoicing, subjects]);
  useEffect(() => {
    const advance = ({ clip, fraction }: ClipProgress) => {
      if (!clip.durationMs) return;
      let ms = fraction * clip.durationMs;
      for (const x of heard.current.get(clip.turn) ?? [])
        if (x.seq < clip.seq) ms += x.durationMs ?? 0;
      subjects.position$.next({ turn: clip.turn, ms });
    };
    const live = player.progress$.subscribe(advance);
    const tapped = replay.progress$.subscribe(advance);
    // A live reply between sentences keeps its place until it is over.
    const stopped = combineLatest([
      subjects.playingTurn$,
      subjects.reveal$,
    ]).subscribe(([turn, reveal]) => {
      const at = subjects.position$.getValue();
      if (turn == null && (at == null || at.turn !== reveal?.turn))
        subjects.position$.next(null);
    });
    return () => {
      live.unsubscribe();
      tapped.unsubscribe();
      stopped.unsubscribe();
    };
  }, [player, replay, subjects]);
  useEffect(() => {
    const subscription = combineLatest([
      player.playing$,
      player.playingTurn$,
      replay.playing$,
    ]).subscribe(([live, liveTurn, tapped]) => {
      subjects.speaking$.next(live || tapped != null);
      subjects.playingTurn$.next(tapped ?? liveTurn);
    });
    const muted = speak$.subscribe(speak => {
      if (!speak) replay.stop();
    });
    return () => {
      subscription.unsubscribe();
      muted.unsubscribe();
    };
  }, [player, replay, speak$, subjects]);

  // The opening plays once, when the scene opens.
  const openingPlayed = useRef(false);
  useEffect(() => {
    if (openingPlayed.current) return;
    openingPlayed.current = true;
    scene.openingAudio.forEach(remember);
    const [first] = scene.openingAudio;
    if (first && speak$.getValue()) startVoicing(first.turn);
    player.enqueue(scene.openingAudio);
  }, [player, remember, scene, speak$, startVoicing]);
  useEffect(() => {
    const events = session.events.subscribe(event => {
      if (event.kind === 'audio') {
        remember(event.clip);
        replay.stop();
        player.enqueue([event.clip]);
      } else if (event.kind === 'line_sent') {
        // A new line, typed or spoken, stops what the customer was saying.
        player.interrupt();
        replay.stop();
        // The reply shares the line's turn.
        if (voicing.current) startVoicing(event.turn);
      }
    });
    return () => events.unsubscribe();
  }, [player, remember, replay, session, startVoicing]);

  /** Talking: the customer stops, and what they said shows in full. */
  const hush = useCallback(() => {
    player.interrupt();
    replay.stop();
    showAll();
  }, [player, replay, showAll]);
  const play = useCallback(
    (turn: number) => {
      player.interrupt();
      void replay.play(turn);
    },
    [player, replay],
  );
  const canPlay = useCallback(
    (turn: number) =>
      scene.target.kind === 'practice' ||
      !!heard.current.get(turn)?.some(x => x.audio),
    [scene],
  );

  return useMemo(
    () => ({
      ...subjects,
      /** The live reply is being spoken; a tap would cut it. */
      live$: player.playing$,
      hush,
      play,
      canPlay,
    }),
    [canPlay, hush, play, player, subjects],
  );
}
